import { type RefObject, useCallback, useEffect, useRef } from 'react'

import { useUISettingsStore } from '@/store/useUISettingsStore'

const DOCUMENT_SELECTOR = '.react-pdf__Document'

/** 交差軸でスクロールできる幅 */
const getCrossRange = (container: HTMLElement, isHorizontal: boolean): number =>
  Math.max(
    0,
    isHorizontal ? container.scrollHeight - container.clientHeight : container.scrollWidth - container.clientWidth,
  )

/**
 * PDF の進行方向と交差する軸（横スクロールなら上下）を、オンにした時点の位置で止める。
 * 交差軸はスクロールさせず、Document を相対位置でずらして固定した位置を出す
 * （スクロールで合わせると、iOS が指でスクロールした瞬間に hidden の軸を 0 に戻してちらつくため）。
 * 返す関数は、ズームやページ移動でレイアウトが変わったあとに呼んで位置を合わせ直す。
 */
export function usePdfScrollLock(
  containerRef: RefObject<HTMLDivElement | null>,
  scrollLock: boolean,
  isHorizontal: boolean,
): () => void {
  const scrollLockRef = useRef(scrollLock)

  const applyLockedAxis = useCallback(() => {
    const container = containerRef.current
    const doc = container?.querySelector<HTMLElement>(DOCUMENT_SELECTOR)
    if (!container || !doc) return
    // ずらす前の寸法で測るため、一度戻す（同じタスク内なので描画はされない）
    doc.style.top = ''
    doc.style.left = ''
    if (!scrollLockRef.current) {
      doc.style.position = ''
      return
    }
    const offset = getCrossRange(container, isHorizontal) * useUISettingsStore.getState().pdfScrollLockRatio
    if (isHorizontal) container.scrollTop = 0
    else container.scrollLeft = 0
    doc.style.position = 'relative'
    doc.style[isHorizontal ? 'top' : 'left'] = `${-offset}px`
  }, [containerRef, isHorizontal])

  // オン/オフを切り替えたとき（開いた時点でオンなら保存済みの位置を使う）
  // 位置はスクロールできる幅に対する比率で持ち、ズームで寸法が変わっても同じ位置を保つ
  useEffect(() => {
    const wasLocked = scrollLockRef.current
    scrollLockRef.current = scrollLock
    const container = containerRef.current
    if (!container || wasLocked === scrollLock) return
    const { pdfScrollLockRatio, setPdfScrollLockRatio } = useUISettingsStore.getState()
    if (scrollLock) {
      const range = getCrossRange(container, isHorizontal)
      const pos = isHorizontal ? container.scrollTop : container.scrollLeft
      // はみ出しが無いときにオンにした場合は中央にする
      setPdfScrollLockRatio(range > 0 ? pos / range : 0.5)
      applyLockedAxis()
      return
    }
    // 解除したら、固定していた位置をスクロールで出し直す
    applyLockedAxis()
    const pos = getCrossRange(container, isHorizontal) * pdfScrollLockRatio
    if (isHorizontal) container.scrollTop = pos
    else container.scrollLeft = pos
  }, [containerRef, scrollLock, isHorizontal, applyLockedAxis])

  // 検索の scrollTo などスクリプトで交差軸がスクロールされたら戻す
  useEffect(() => {
    const container = containerRef.current
    if (!scrollLock || !container) return
    const handleScroll = () => {
      if ((isHorizontal ? container.scrollTop : container.scrollLeft) !== 0) applyLockedAxis()
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [containerRef, scrollLock, isHorizontal, applyLockedAxis])

  return applyLockedAxis
}
